import { Loader2, Inbox, AlertTriangle } from "lucide-react";
import { toneClass, CUSTOMER_REQUEST_LABEL } from "@/lib/format";

export function StatusBadge({ status, customer = false, testId }) {
  const label = customer ? CUSTOMER_REQUEST_LABEL[status] || status : status;
  return (
    <span data-testid={testId || "status-badge"} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${toneClass(label)}`}>
      {label}
    </span>
  );
}

export const DemoTag = ({ show }) =>
  show ? (
    <span data-testid="demo-tag" className="ml-2 inline-flex rounded border border-dashed border-amber-400 bg-amber-50 px-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
      Demo
    </span>
  ) : null;

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export const Loading = ({ label = "Loading…" }) => (
  <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground" role="status" data-testid="loading-state">
    <Loader2 className="h-4 w-4 animate-spin" /> {label}
  </div>
);

export const ErrorBox = ({ error }) =>
  error ? (
    <div role="alert" data-testid="error-message" className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{error}</span>
    </div>
  ) : null;

export const EmptyState = ({ title, text, action, testId = "empty-state" }) => (
  <div data-testid={testId} className="flex flex-col items-center rounded-lg border border-dashed border-border bg-card px-6 py-10 text-center">
    <Inbox className="mb-3 h-8 w-8 text-muted-foreground" />
    <p className="font-medium">{title}</p>
    {text && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Section = ({ title, children, actions, className = "", testId }) => (
  <section data-testid={testId} className={`rounded-lg border border-border bg-card p-5 ${className}`}>
    {(title || actions) && (
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="font-heading text-base font-semibold md:text-lg">{title}</h2>
        {actions}
      </div>
    )}
    {children}
  </section>
);

export const Field = ({ label, id, error, hint, children, required }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="text-sm font-medium text-foreground">
      {label} {required && <span className="text-rose-700" aria-hidden>*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    {error && <p className="text-xs text-rose-700" role="alert">{error}</p>}
  </div>
);

export const NativeSelect = ({ className = "", children, ...props }) => (
  <select
    {...props}
    className={`flex h-10 w-full rounded-md border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}
  >
    {children}
  </select>
);

export const Detail = ({ label, children }) => (
  <div>
    <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</dt>
    <dd className="mt-0.5 text-sm text-foreground break-words">{children || "—"}</dd>
  </div>
);
